import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { v4 } from 'uuid';
import Carousel from 'Common/components/ui/Carousel';
import { useTranslator, useViewport } from 'Common/hooks';
import { youtubeThumbnailResolver } from 'Common/utils';
import { VideoPlayer, VideoPlayerModal } from '../ui';

const VideoGalleryItem = ({ item, index, visibleIndices, onClickVideo, thumbTranslator }) => {
    if (!item?.Url || item.MediaType !== 'video' || !item.ThumbnailUrl) {
        return null;
    }

    const isVisible = visibleIndices?.includes(index);

    return (
        <div
            className={cx('VideoGallery__item', {
                'VideoGallery__item--active': isVisible,
            })}
            onClick={() => onClickVideo(item)}
            aria-label={thumbTranslator(item)}
            role="button"
            tabIndex="0"
            onKeyPress={(e) => {
                if (e.key === 'Enter') {
                    onClickVideo(item);
                }
            }}
        >
            <div
                className="VideoGallery__videoThumb"
                style={{
                    backgroundImage: `url(${item.ThumbnailUrl})`,
                }}
            >
                <i className="far fa-play-circle"></i>
            </div>
        </div>
    );
};

VideoGalleryItem.propTypes = {
    item: px.object,
    index: px.number,
    visibleIndices: px.arrayOf(px.number),
    onClickVideo: px.func,
    thumbTranslator: px.func,
};

export default function VideoGallery({
    arrowColor = 'default',
    shouldOverhang = false,
    videos = [],
}) {
    const id = React.useMemo(() => `video-${v4()}`, []);
    const thumbTranslator = useTranslator('Commerce.Product.Gallery.SelectThumb.Label');
    const viewport = useViewport();
    const [showModal, setShowModal] = useState(false);
    const [videoData, setVideoData] = useState(null);

    // Determine if mobile based on viewport
    const isMobile = useMemo(() => viewport.is.lt('md'), [viewport]);

    const containerClasses = cx(
        'VideoGallery',
        'carousel',
        {
            'carousel--overhang': shouldOverhang,
        },
        `carousel--arrow-color-${arrowColor}`
    );

    const onCloseModal = useCallback(() => {
        setShowModal(false);
        setVideoData(null);
    }, []);

    const onClickGalleryItem = useCallback((selectedVideo) => {
        setVideoData({
            url: selectedVideo.Url,
            previewImage: selectedVideo.ThumbnailUrl,
            playing: true,
        });
        setShowModal(true);
    }, []);

    // Filter valid videos
    const validVideos = useMemo(
        () => videos.filter((v) => v?.Url && v.MediaType === 'video' && v.ThumbnailUrl),
        [videos]
    );

    // Resolve YouTube thumbnails for carousel items
    useLayoutEffect(() => {
        if (validVideos.length > 1) {
            youtubeThumbnailResolver(`#${id} .VideoGallery__videoThumb`);
        }
    }, [id, validVideos]);

    const videosCount = validVideos.length;

    // Use isMobile to determine carousel sizing
    // Mobile: show 1 item at a time
    // Desktop: show fixed-width thumbnails
    const carouselItemsPerView = isMobile ? 1 : null;
    const carouselItemWidth = isMobile ? '100%' : 600;

    return (
        <div className={containerClasses} id={id}>
            <div>
                {videosCount === 0 ? (
                    <div>No videos to display</div>
                ) : videosCount === 1 ? (
                    <div className="VideoGallery__single">
                        <VideoPlayer
                            url={validVideos[0].Url}
                            previewImage={validVideos[0].ThumbnailUrl}
                            playing={false}
                            playsInline
                            style={{ width: '100%', height: '450px' }}
                        />
                    </div>
                ) : (
                    <Carousel
                        id={`${id}-carousel`}
                        items={validVideos}
                        keyBy={(video) => video.Url}
                        Item={VideoGalleryItem}
                        itemProps={{
                            onClickVideo: onClickGalleryItem,
                            thumbTranslator,
                        }}
                        itemsPerView={carouselItemsPerView}
                        itemWidth={carouselItemWidth}
                        gap={15}
                        BtnScrollLeft={<i className="fas fa-chevron-left" aria-hidden="true" />}
                        BtnScrollRight={<i className="fas fa-chevron-right" aria-hidden="true" />}
                        animateScroll={true}
                    />
                )}
            </div>
            {showModal && videoData && (
                <VideoPlayerModal videoProps={videoData} show={showModal} onClose={onCloseModal} />
            )}
        </div>
    );
}

VideoGallery.propTypes = {
    arrowColor: px.string,
    shouldOverhang: px.bool,
    videos: px.arrayOf(px.object),
};